import React, { useCallback, useMemo } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import CommonInput from '../../component/form/common-input.component';
import { ReactComponent as SearchIconSvg } from '../../static/images/svg/ic_round-search.svg';
import { ReactComponent as CloseIconSvg } from '../../static/images/svg/mingcute_close-fill.svg';
import { TableVirtuoso, type TableComponents } from 'react-virtuoso';
import i18n from '../../i18n';
import { formatEUR } from '../../constants/tool.fn';
import type { GroupMenuCatalogOptionItem } from '../../request/api/group-revenue-analysis';
import { MENU_LIBRARY_DISH_ITEM_TYPE_OPTIONS } from '../../utils/menu-library-dish-item-type-options';

/** 空单元格占位符 */
export const SEARCH_TABLE_DASH = '-';

/** 弹窗内次要说明文字色（与筛选弹窗预览区一致，可复用） */
export const SEARCH_TABLE_TEXT_SECONDARY = 'rgba(31, 41, 64, 0.5)';

/** 预览区统计徽章内文字色 */
export const SEARCH_TABLE_NAME_SUB = '#94a3b8';

/**
 * 搜索结果行：菜单命中仅填菜单列；类别 / 菜品行结构不同。
 */
export type DishSalesSearchRow =
    | { kind: 'menu'; key: string; menuName: string }
    | { kind: 'category'; key: string; menuName: string; categoryName: string }
    | { kind: 'dish'; key: string; dish: GroupMenuCatalogOptionItem };

/** 列宽（与 Figma 120+120+240+120+88 比例一致） */
const COL_WIDTH = {
    menu: { width: '17.4%' },
    id: { width: '17.4%' },
    name: { width: '34.9%' },
    cat: { width: '17.4%' },
    price: { width: '12.9%' },
    dishType: { width: '12.9%' },
} as const;

type ColKey = keyof typeof COL_WIDTH;

type VAlign = 'top' | 'middle';

/** 各列纵向对齐（名称列多行时顶对齐） */
const COL_VERTICAL_ALIGN: Record<ColKey, VAlign> = {
    menu: 'middle',
    id: 'middle',
    name: 'top',
    cat: 'middle',
    price: 'middle',
    dishType: 'middle',
};

/** 表头列配置：文案 key 与对齐 */
const HEADER_COLUMNS: ReadonlyArray<{
    col: ColKey;
    i18nKey: string;
    align: 'left' | 'center' | 'right';
}> = [
        { col: 'menu', i18nKey: 'group_revenue_analysis_search_col_menu', align: 'center' },
        { col: 'cat', i18nKey: 'group_revenue_analysis_search_col_category', align: 'center' },
        { col: 'id', i18nKey: 'group_revenue_analysis_search_col_dish_id', align: 'center' },
        { col: 'name', i18nKey: 'group_revenue_analysis_search_col_dish_name', align: 'left' },
        { col: 'dishType', i18nKey: 'menu_library_detail_col_type', align: 'center' },
        { col: 'price', i18nKey: 'group_revenue_analysis_search_col_price', align: 'center' },
    ];

/**
 * 表头样式。`Table size="small"` 下需覆盖 `MuiTableCell-head` / `sizeSmall` 的 padding。
 */
const HEAD_CELL_SX = {
    backgroundColor: 'var(--fill-primary-200)',
    fontWeight: 500,
    color: 'var(--fill-primary-300)',
    borderBottom: 'none',
    fontSize: 16,
    lineHeight: '24px',
    px: 2,
    boxSizing: 'border-box' as const,
    '&.MuiTableCell-root.MuiTableCell-head': {
        paddingTop: '12px',
        paddingBottom: '12px',
        minHeight: 48,
    }
} as const;

/** 菜品 ID 列展示为 #xxx */
function formatDishIdForTable(id?: string): string {
    const s = String(id ?? '').trim();
    if (!s) return SEARCH_TABLE_DASH;
    return s.startsWith('#') ? s : `#${s}`;
}

/**
 * 菜品类型列：与 `menu-library-management-state.getDishTypeLabel` / `ITEM_TYPE` 数值一致。
 * @param raw - 接口 `item_type`（常见为数字字符串）
 */
function formatDishItemTypeForSearchTable(raw: string | number | undefined | null): string {
    if (raw === undefined || raw === null || raw === '') {
        return SEARCH_TABLE_DASH;
    }
    const n = typeof raw === 'number' ? raw : Number.parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(n)) {
        return SEARCH_TABLE_DASH;
    }
    const hit = MENU_LIBRARY_DISH_ITEM_TYPE_OPTIONS.find((x) => x.value === n);
    return hit ? i18n.t(hit.labelKey) : SEARCH_TABLE_DASH;
}

/** 数据行基础单元格样式 */
function dataCellBase(isSelected: boolean) {
    return {
        border: 'none',
        py: 1.5,
        px: 2,
        fontSize: 16,
        fontWeight: 500,
        lineHeight: '24px',
        verticalAlign: 'top' as const,
        color: isSelected ? '#ffffff' : 'var(--text-dark)',
        wordBreak: 'break-word' as const,
        overflowWrap: 'break-word' as const,
    };
}

/** 未选中 / 选中行单元格基础样式（稳定引用，减轻滚动时 MUI `sx` 比对与合并开销） */
const DATA_CELL_BASE_UNSELECTED = dataCellBase(false);
const DATA_CELL_BASE_SELECTED = dataCellBase(true);

/** 数据行 `TableRow` 的交互样式（稳定引用） */
const SEARCH_TABLE_DATA_ROW_SX = {
    cursor: 'pointer' as const,
    '&.Mui-selected': {
        bgcolor: 'var(--fill-primary-300)',
        '&:hover': { bgcolor: 'var(--fill-primary-300)' },
    },
    '&:not(.Mui-selected):hover': {
        bgcolor: 'rgba(31, 41, 64, 0.04)',
    },
} as const;

/** 数据单元格：统一合并列宽与对齐 */
function SearchResultDataCell(props: {
    base: ReturnType<typeof dataCellBase>;
    col: ColKey;
    textAlign: 'left' | 'center' | 'right';
    verticalAlign?: VAlign;
    children: React.ReactNode;
}) {
    const { base, col: colKey, textAlign, verticalAlign = 'middle', children } = props;
    return (
        <TableCell
            sx={{
                ...base,
                ...COL_WIDTH[colKey],
                textAlign,
                verticalAlign,
            }}
        >
            {children}
        </TableCell>
    );
}

/** 菜品名称双行（主名 + 描述），行内字号与颜色继承单元格 */
const DISH_NAME_STACK_SX = {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: 0.5,
    minWidth: 0,
    '& > span': {
        fontSize: 16,
        fontWeight: 500,
        lineHeight: '24px',
        color: 'inherit',
    },
};

/**
 * 按行类型组装各列展示内容（与 `HEADER_COLUMNS` 列顺序一致）。
 */
function buildSearchRowCells(row: DishSalesSearchRow): Record<ColKey, React.ReactNode> {
    const dash = SEARCH_TABLE_DASH;
    if (row.kind === 'menu') {
        return {
            menu: row.menuName,
            id: dash,
            name: dash,
            cat: dash,
            price: dash,
            dishType: dash,
        };
    }
    if (row.kind === 'category') {
        return {
            menu: row.menuName || dash,
            id: dash,
            name: dash,
            cat: row.categoryName,
            price: dash,
            dishType: dash,
        };
    }
    const d = row.dish;
    const desc = d.item_description?.trim();
    const priceDisplay = d.price != null && Number.isFinite(d.price) ? formatEUR(d.price) : '-';
    return {
        menu: d.menu_name ?? dash,
        id: formatDishIdForTable(d.item_id),
        name: (
            <Box sx={DISH_NAME_STACK_SX}>
                <span>{d.name}</span>
                {desc ? <span>{desc}</span> : null}
            </Box>
        ),
        cat: d.category_name ?? dash,
        price: priceDisplay,
        dishType: formatDishItemTypeForSearchTable(d.item_type),
    };
}

/**
 * TableVirtuoso 的 context：选中态走 context，避免 `components` 依赖 selectedKey 导致引用变化、滚动条跳回顶部。
 */
interface DishSalesSearchTableVirtuosoContext {
    /** 当前选中行 key */
    selectedKey: string | null;
    /** 选中行变更 */
    onSelectKey: (key: string) => void;
    /** 无数据占位（EmptyPlaceholder 使用） */
    emptyText: string;
}

/** react-virtuoso 传入的 EmptyPlaceholder props（具名组件 + 显式类型，满足 eslint react/prop-types） */
type VirtuosoEmptyPlaceholderProps = { context: DishSalesSearchTableVirtuosoContext };

function DishSalesVirtuosoEmptyPlaceholder({ context }: VirtuosoEmptyPlaceholderProps) {
    return (
        <TableRow>
            <TableCell
                colSpan={6}
                align="center"
                sx={{
                    py: 3,
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: '24px',
                    color: SEARCH_TABLE_TEXT_SECONDARY,
                    border: 'none',
                }}
            >
                {context.emptyText}
            </TableCell>
        </TableRow>
    );
}

export interface DishSalesSearchResultTableProps {
    /** 虚拟列表数据行 */
    rows: DishSalesSearchRow[];
    /** 当前选中行的 key */
    selectedKey: string | null;
    /** 选中行变更 */
    onSelectKey: (key: string) => void;
    /** 无数据时占位文案 */
    emptyText: string;
}

/**
 * 集团菜品销售分析 · 搜索弹窗结果表（虚拟滚动）。
 */
export function DishSalesSearchResultTable(props: DishSalesSearchResultTableProps) {
    const { rows, selectedKey, onSelectKey, emptyText } = props;
    console.log('rows', rows);

    /** 与选中态、占位文案同步；勿把上述字段放进 `components` 的 useMemo，否则会重置 Virtuoso 滚动位置 */
    const virtuosoContext = useMemo(
        (): DishSalesSearchTableVirtuosoContext => ({
            selectedKey,
            onSelectKey,
            emptyText,
        }),
        [selectedKey, onSelectKey, emptyText],
    );

    const components = useMemo((): TableComponents<DishSalesSearchRow, DishSalesSearchTableVirtuosoContext> => {
        return {
            /**
             * 使用原生 div 作为滚动根节点（勿用 MUI TableContainer/Paper）：
             * Virtuoso 会注入 `height:100%`、`overflowY:auto` 等与测量强相关的 style；
             * TableContainer 额外包装易导致视口高度/滚动区域计算异常，滚动时出现空白行。
             */
            Scroller: React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(function VirtuosoScroller(
                p,
                ref,
            ) {
                return (
                    <div
                        {...p}
                        ref={ref}
                        style={{
                            ...(p.style as React.CSSProperties | undefined),
                            backgroundColor: '#fff',
                            borderRadius: 0,
                        }}
                    />
                );
            }),
            Table: (p: React.ComponentProps<typeof Table>) => (
                <Table {...p} size="small" sx={{ borderCollapse: 'separate', tableLayout: 'fixed', width: '100%' }} />
            ),
            TableHead: React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
                function VirtuosoTableHead(p, ref) {
                    return <TableHead {...p} ref={ref} />;
                },
            ),
            TableBody: React.forwardRef<HTMLTableSectionElement, React.HTMLAttributes<HTMLTableSectionElement>>(
                function VirtuosoTableBody(p, ref) {
                    return <TableBody {...p} ref={ref} />;
                },
            ),
            TableRow: (
                p: React.ComponentProps<typeof TableRow> & {
                    item?: DishSalesSearchRow;
                    context: DishSalesSearchTableVirtuosoContext;
                },
            ) => {
                const { item, context, ...rowProps } = p;
                const row = item;
                return (
                    <TableRow
                        {...rowProps}
                        hover
                        selected={row != null && context.selectedKey === row.key}
                        onClick={() => row && context.onSelectKey(row.key)}
                        sx={row ? SEARCH_TABLE_DATA_ROW_SX : undefined}
                    />
                );
            },
            EmptyPlaceholder: DishSalesVirtuosoEmptyPlaceholder,
        };
    }, []);

    const fixedHeaderContent = useCallback(
        () => (
            <TableRow sx={{ '& .MuiTableCell-head': { verticalAlign: 'middle' } }}>
                {HEADER_COLUMNS.map(({ col, i18nKey, align }) => (
                    <TableCell
                        key={i18nKey}
                        variant="head"
                        scope="col"
                        sx={{ ...HEAD_CELL_SX, ...COL_WIDTH[col], textAlign: align }}
                    >
                        {i18n.t(i18nKey)}
                    </TableCell>
                ))}
            </TableRow>
        ),
        [],
    );

    const itemContent = useCallback(
        (_index: number, row: DishSalesSearchRow, ctx: DishSalesSearchTableVirtuosoContext) => {
            const c = ctx.selectedKey === row.key ? DATA_CELL_BASE_SELECTED : DATA_CELL_BASE_UNSELECTED;
            const cells = buildSearchRowCells(row);
            return (
                <>
                    {HEADER_COLUMNS.map(({ col, align }) => (
                        <SearchResultDataCell
                            key={col}
                            base={c}
                            col={col}
                            textAlign={align}
                            verticalAlign={COL_VERTICAL_ALIGN[col]}
                        >
                            {cells[col]}
                        </SearchResultDataCell>
                    ))}
                </>
            );
        },
        [],
    );

    // 显式关闭 scroll seek：未配置时库仍可能进入占位模式，用空 tr 替换真实行导致滚动时一片白
    return (
        <TableVirtuoso<DishSalesSearchRow, DishSalesSearchTableVirtuosoContext>
            style={{ height: 360, width: '100%' }}
            data={rows}
            components={components}
            context={virtuosoContext}
            fixedHeaderContent={fixedHeaderContent}
            defaultItemHeight={80}
            increaseViewportBy={240}
            scrollSeekConfiguration={false}
            computeItemKey={(_, row) => row.key}
            itemContent={itemContent}
        />
    );
}

/** 集团菜品销售分析 · 菜单搜索弹窗（搜索框 + 结果预览 + 虚拟表格 + 底栏） */
export interface DishSalesSearchDialogProps {
    /** 是否打开 */
    open: boolean;
    /** 关闭弹窗 */
    onClose: () => void;
    /** 搜索框受控值 */
    searchDraft: string;
    /** 搜索框内容变更 */
    onSearchDraftChange: (value: string) => void;
    /** 虚拟表格数据行 */
    searchResultRows: DishSalesSearchRow[];
    /** 当前选中行 key */
    selectedSearchRowKey: string | null;
    /** 选中行变更 */
    onSelectSearchRowKey: (key: string) => void;
    /** 是否可点确定 */
    canApply: boolean;
    /** 确定：应用筛选并关闭由父组件处理 */
    onApply: () => void;
    /** 表格无匹配结果时的占位文案 */
    emptyTableText: string;
}

/**
 * 菜单本地搜索弹窗（与骨架屏、主内容共用，避免加载时关闭后无法再次打开）
 */
export function DishSalesSearchDialog(props: DishSalesSearchDialogProps) {
    const {
        open,
        onClose,
        searchDraft,
        onSearchDraftChange,
        searchResultRows,
        selectedSearchRowKey,
        onSelectSearchRowKey,
        canApply,
        onApply,
        emptyTableText,
    } = props;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '16px',
                    border: '2px solid var(--fill-primary-300)',
                    overflow: 'hidden',
                    bgcolor: '#fff',
                },
            }}
        >
            <DialogTitle
                component="div"
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 1,
                    pt: 2,
                    px: 3,
                    pb: 0,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SearchIconSvg
                        style={{ width: 32, height: 32, flexShrink: 0, color: 'var(--fill-primary-300)' }}
                        aria-hidden
                    />
                    <Typography
                        component="span"
                        sx={{
                            fontSize: 24,
                            lineHeight: '32px',
                            fontWeight: 700,
                            color: 'var(--text-dark)',
                        }}
                    >
                        {i18n.t('group_revenue_analysis_dish_sales_search_dialog_title')}
                    </Typography>
                </Box>
                <IconButton
                    type="button"
                    size="small"
                    aria-label={i18n.t('extra_cost_text_close')}
                    onClick={onClose}
                    sx={{ p: 0.5, borderRadius: '40px' }}
                >
                    <CloseIconSvg style={{ width: 24, height: 24 }} />
                </IconButton>
            </DialogTitle>
            <DialogContent
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 3,
                    paddingTop: '16px !important',
                    px: 3,
                    pb: 2,
                }}
            >
                <CommonInput
                    inputType="INPUT"
                    size="small"
                    placeholder={i18n.t('group_revenue_analysis_search_placeholder')}
                    value={searchDraft}
                    autoFocus
                    onChange={(e) => onSearchDraftChange(e.target.value)}
                    sx={{
                        width: '100%',
                        minWidth: 0,
                        '& .MuiOutlinedInput-root': {
                            minHeight: 40,
                            borderRadius: '16px',
                            bgcolor: '#fff',
                            boxShadow: '0px 0px 4px 0px rgba(31, 41, 64, 0.5)',
                            px: 2,
                            py: 0.5,
                        },
                        '& .MuiOutlinedInput-input': {
                            fontSize: 20,
                            lineHeight: '24px',
                            fontWeight: 500,
                            color: 'var(--text-dark)',
                        },
                    }}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton size="small" edge="end" tabIndex={-1} aria-label="search">
                                    <SearchIconSvg
                                        style={{ width: 24, height: 24, color: 'var(--fill-primary-300)' }}
                                    />
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
                {(
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%', minWidth: 0 }}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 1,
                                width: '100%',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontSize: 16,
                                    fontWeight: 500,
                                    lineHeight: '24px',
                                    color: SEARCH_TABLE_TEXT_SECONDARY,
                                }}
                            >
                                {i18n.t('menu_library_filter_preview_title')}
                            </Typography>
                            <Box
                                component="span"
                                sx={{
                                    bgcolor: 'var(--fill-neutral-200)',
                                    px: 0.75,
                                    py: 0.25,
                                    borderRadius: '8px',
                                }}
                            >
                                <Typography
                                    component="span"
                                    sx={{
                                        fontSize: 16,
                                        fontWeight: 500,
                                        lineHeight: '24px',
                                        color: SEARCH_TABLE_NAME_SUB,
                                    }}
                                >
                                    {i18n.t('menu_library_filter_preview_count', { count: searchResultRows.length })}
                                </Typography>
                            </Box>
                        </Box>
                        <DishSalesSearchResultTable
                            rows={searchResultRows}
                            selectedKey={selectedSearchRowKey}
                            onSelectKey={onSelectSearchRowKey}
                            emptyText={emptyTableText}
                        />
                    </Box>
                )}
            </DialogContent>
            <DialogActions
                sx={{
                    justifyContent: 'flex-end',
                    gap: 1,
                    px: 6,
                    py: 2,
                    pb: 6,
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 5,
                        py: 2,
                        borderColor: '#e2e8f0',
                        bgcolor: '#fff',
                        color: 'var(--text-dark)',
                        fontSize: 16,
                        fontWeight: 500,
                        lineHeight: '24px',
                        '&:hover': { borderColor: '#e2e8f0', bgcolor: '#fafafa' },
                    }}
                >
                    {i18n.t('general_button_cancel')}
                </Button>
                <Button
                    variant="contained"
                    disabled={!canApply}
                    onClick={onApply}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '8px',
                        px: 5,
                        py: 2,
                        fontSize: 16,
                        fontWeight: 500,
                        lineHeight: '24px',
                        bgcolor: 'var(--fill-primary-300)',
                        boxShadow: '0px 1px 2px 0px rgba(0, 0, 0, 0.05)',
                        '&:hover': { bgcolor: 'var(--fill-primary-300)', filter: 'brightness(0.95)' },
                        '&.Mui-disabled': { bgcolor: 'rgba(116, 104, 240, 0.35)', color: '#fff' },
                    }}
                >
                    {i18n.t('general_button_confirm')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
